import XLSX from "xlsx-js-style";
import {
  append_export_answer_to_row_if_needed,
  get_export_row_values,
  get_export_value_text,
  get_survey_result_export_value_from_header,
} from "./survey_export_selectors";

function get_survey_result_names(user) {
  const first_name = String(user?.first_name || "").trim();
  const last_name = String(user?.last_name || "").trim();
  const full_name = `${first_name} ${last_name}`.trim();

  return {
    full_name,
    is_blocked: Boolean(user?.is_blocked),
  };
}

function get_survey_result_user_name(user) {
  const { full_name, is_blocked } = get_survey_result_names(user);
  return is_blocked ? "Unknown User" : full_name || "Unknown User";
}

function get_survey_result_response_key(user_id, survey_question, question_index, answer_index) {
  if (user_id !== undefined && user_id !== null) {
    return `user-${user_id}`;
  }

  return `anonymous-${survey_question?.id || question_index}-${answer_index}`;
}

function get_survey_result_row(respondents, response_key, user) {
  if (!respondents.has(response_key)) {
    respondents.set(response_key, {
      respondent_name: get_survey_result_user_name(user),
      respondent_email: user?.email || "",
      answers: {},
    });
  }

  return respondents.get(response_key);
}

function append_export_answer(row, question_text, next_answer) {
  const current_answer = Reflect.get(row.answers, question_text);

  if (!current_answer) {
    Reflect.set(row.answers, question_text, next_answer);
    return;
  }

  const values = String(current_answer || "")
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);

  if (!values.includes(next_answer)) {
    values.push(next_answer);
  }

  Reflect.set(row.answers, question_text, values.join(", "));
}

function get_export_row_cell_length(row, index) {
  return String(Reflect.get(row || [], index) || "").length;
}

function get_export_column_widths(headers, rows) {
  return headers.map((header, index) => {
    const header_length = String(header || "").length;
    const max_row_length = Math.max(...rows.map((row) => get_export_row_cell_length(row, index)), 0);
    const max_length = Math.max(header_length, max_row_length);

    return ({
      wch: Math.min(Math.max(max_length + 4, 18), 60),
    });
  });
}

function get_export_header_style() {
  return ({
    font: { bold: true, color: { rgb: "FFFFFF" } },
    fill: { fgColor: { rgb: "1F4E78" } },
    alignment: { horizontal: "center", vertical: "center", wrapText: true },
    border: {
      top: { style: "thin", color: { rgb: "D9D9D9" } },
      bottom: { style: "thin", color: { rgb: "D9D9D9" } },
      left: { style: "thin", color: { rgb: "D9D9D9" } },
      right: { style: "thin", color: { rgb: "D9D9D9" } },
    },
  });
}

function get_export_data_cell_style() {
  return ({
    alignment: { vertical: "top", wrapText: true },
    border: {
      top: { style: "thin", color: { rgb: "E5E5E5" } },
      bottom: { style: "thin", color: { rgb: "E5E5E5" } },
      left: { style: "thin", color: { rgb: "E5E5E5" } },
      right: { style: "thin", color: { rgb: "E5E5E5" } },
    },
  });
}

function get_export_link_cell_style() {
  return ({
    ...get_export_data_cell_style(),
    font: { color: { rgb: "0563C1" }, underline: true },
  });
}

function format_export_value(value) {
  return String(get_export_value_text(value) ?? "").trim();
}

function get_survey_result_export_value(data, header) {
  if (header === "Full Names") {
    return data.respondent_name;
  }

  if (header === "Email") {
    return data.respondent_email;
  }

  return get_survey_result_export_value_from_header(data, header, "answer");
}
function build_export_headers(survey) {
  return [
    "Full Names",
    "Email",
    ...(Array.isArray(survey?.survey_questions)
      ? survey.survey_questions.map((item, index) => item?.question?.question || `Question ${index + 1}`)
      : []),
  ];
}

function build_export_rows(survey_results, export_headers) {
  return survey_results.map((data) =>
    export_headers.map((header) => format_export_value(get_survey_result_export_value(data, header)))
  );
}

function prepare_worksheet(export_headers, export_rows) {
  const worksheet = XLSX.utils.aoa_to_sheet([export_headers, ...export_rows]);
  const range = XLSX.utils.decode_range(Reflect.get(worksheet, "!ref"));

  Reflect.set(worksheet, "!cols", get_export_column_widths(export_headers, export_rows));
  Reflect.set(worksheet, "!rows", [{ hpt: 28 }]);
  Reflect.set(worksheet, "!autofilter", { ref: Reflect.get(worksheet, "!ref") });

  export_headers.forEach((_, index) => {
    const cell_ref = XLSX.utils.encode_cell({ r: 0, c: index });
    const worksheet_cell = Reflect.get(worksheet, cell_ref);

    if (worksheet_cell) {
      Reflect.set(worksheet_cell, "s", get_export_header_style());
    }
  });

  for (let row = 1; row <= range.e.r; row++) {
    for (let col = 0; col <= range.e.c; col++) {
      const cell_ref = XLSX.utils.encode_cell({ r: row, c: col });
      const worksheet_cell = Reflect.get(worksheet, cell_ref);

      if (worksheet_cell) {
        Reflect.set(worksheet_cell, "s", get_export_data_cell_style());
      }
    }
  }

  return worksheet;
}
function get_export_cell_hyperlink(cell_value) {
  if (!cell_value || typeof cell_value !== "object") return null;

  return Reflect.get(cell_value, "hyperlink");
}

function apply_export_link_to_cell(worksheet, cell_value, row_index, col_index) {
  const hyperlink = get_export_cell_hyperlink(cell_value);

  if (!hyperlink) return;

  const cell_ref = XLSX.utils.encode_cell({ r: row_index + 1, c: col_index });
  const worksheet_cell = Reflect.get(worksheet, cell_ref);

  if (!worksheet_cell) return;

  Reflect.set(worksheet_cell, "l", {
    Target: hyperlink,
    Tooltip: format_export_value(cell_value),
  });
  Reflect.set(worksheet_cell, "s", get_export_link_cell_style());
}

function apply_export_links(worksheet, raw_export_rows) {
  raw_export_rows.forEach((row, row_index) => {
    get_export_row_values(row).forEach((cell_value, col_index) => {
      apply_export_link_to_cell(worksheet, cell_value, row_index, col_index);
    });
  });
}

function create_respondent_export_row(user) {
  return {
    respondent_name: get_survey_result_user_name(user),
    respondent_email: user?.email || "",
    answers: {},
  };
}

function append_export_answer_to_row(instance, respondents, survey_question, question_text, question_index, answer, answer_index) {
  return append_export_answer_to_row_if_needed(
    instance,
    respondents,
    survey_question,
    question_text,
    question_index,
    answer,
    answer_index
  );
}

function write_survey_results_export_file(instance, survey, survey_results, export_headers) {
  const raw_export_rows = survey_results.map((application) =>
    export_headers.map((header) => instance.get_survey_results_export_value(application, header))
  );
  const export_rows = instance.build_survey_results_export_rows(survey_results, export_headers);
  const worksheet = instance.prepare_survey_results_worksheet(export_headers, export_rows);

  instance.apply_survey_results_excel_links(worksheet, raw_export_rows);

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Survey Results");
  XLSX.writeFile(workbook, instance.get_survey_results_export_file_name(survey));
}

export {
  append_export_answer_to_row,
  append_export_answer,
  apply_export_links,
  build_export_headers,
  build_export_rows,
  create_respondent_export_row,
  format_export_value,
  get_export_column_widths,
  get_export_data_cell_style,
  get_export_header_style,
  get_export_link_cell_style,
  get_survey_result_export_value,
  get_survey_result_response_key,
  get_survey_result_row,
  get_survey_result_user_name,
  prepare_worksheet,
  write_survey_results_export_file,
};
